// canvas/bid-bubbles.js: T10 persistent per-seat Pass markers.
//
// Bot annotations fade out after a few seconds, so mid-bid the table
// forgot who had already passed, the player had to replay the bid
// round in their head. During bid_r1/bid_r2 every seat that has passed
// THIS round keeps a quiet "Pass" pill straddling the table-facing
// edge of its nameplate until the round resolves.
//
// Round scoping: reaching bid_r2 means everyone passed r1 by
// definition, so r1 passes are noise there, only the current round's
// passes render.

const COLOR_BG     = 'rgba(31, 24, 40, 0.92)';   // --color-surface
const COLOR_BORDER = 'rgba(154, 138, 184, 0.6)'; // --color-text-muted
const COLOR_TEXT   = '#9A8AB8';
const BUBBLE_W = 52;
const BUBBLE_H = 20;
const FONT = '700 11px ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Seats that passed in the CURRENT bid round. Accepts bid_history as an
 * array or the raw Lua-table-as-object wire shape.
 *
 * @param {{current_state?: string, bid_history?: any}|null|undefined} state
 * @returns {Set<number>}
 */
export function passedSeats(state) {
  const out = new Set();
  const cs = state?.current_state;
  if (cs !== 'bid_r1' && cs !== 'bid_r2') return out;
  const round = cs === 'bid_r2' ? 2 : 1;
  const hist = state?.bid_history;
  const entries = Array.isArray(hist) ? hist : Object.values(hist || {});
  for (const b of entries) {
    if (b && b.action === 'pass' && b.round === round && typeof b.seat === 'number') {
      out.add(b.seat);
    }
  }
  return out;
}

/**
 * Pill rect straddling the table-facing edge of the seat's nameplate.
 * Pure, exported for the positioning test.
 *
 * @param {{align:'bottom'|'left'|'top'|'right', x:number, y:number, w:number, h:number,
 *   pillSide?: 'top'|'bottom'|'left'|'right'}} seat
 * @returns {{x:number, y:number, w:number, h:number}}
 */
export function bubbleRectForSeat(seat) {
  const w = BUBBLE_W;
  const h = BUBBLE_H;
  // Y4/Z5: the short-landscape layout names the edge. Its pills sit just
  // outside the plate (sharing that edge), so they never cover the name.
  // 'left'/'right' pills sit at the plate's bottom corner, under the fan.
  if (seat.pillSide === 'top') {
    return { x: seat.x + Math.round((seat.w - w) / 2), y: seat.y - h, w, h };
  }
  if (seat.pillSide === 'bottom') {
    return { x: seat.x + Math.round((seat.w - w) / 2), y: seat.y + seat.h, w, h };
  }
  if (seat.pillSide === 'right') {
    return { x: seat.x + seat.w, y: seat.y + seat.h - h, w, h };
  }
  if (seat.pillSide === 'left') {
    return { x: seat.x - w, y: seat.y + seat.h - h, w, h };
  }
  // Z7: only the portrait composition (44px plates) reaches this rule.
  if (seat.h <= 44) {
    return { x: seat.x + Math.round((seat.w - w) / 2), y: seat.y + seat.h - Math.round(h / 2), w, h };
  }
  switch (seat.align) {
    case 'bottom': return { x: seat.x + Math.round((seat.w - w) / 2), y: seat.y - Math.round(h / 2), w, h };
    case 'top':    return { x: seat.x + Math.round((seat.w - w) / 2), y: seat.y + seat.h - Math.round(h / 2), w, h };
    case 'left':   return { x: seat.x + seat.w - Math.round(w / 2), y: seat.y + Math.round((seat.h - h) / 2), w, h };
    case 'right':
    default:       return { x: seat.x - Math.round(w / 2), y: seat.y + Math.round((seat.h - h) / 2), w, h };
  }
}

/**
 * Render the Pass pills for the current bid round. No-op outside
 * bid states or when nobody has passed.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('./layout.js').TableLayout} layout
 * @param {any} state
 */
export function renderBidBubbles(ctx, layout, state) {
  const passed = passedSeats(state);
  // B3c r3 e2e seam: "slot:x,y,w,h" for every pill drawn this frame
  // (slot 0 is the viewer's own plate), so a browser test reads the
  // drawn pills, the viewer's included, rather than recomputing them.
  const el = ctx.canvas;
  /** @type {string[]} */
  const drawn = [];
  if (passed.size === 0) {
    if (el && el.dataset) delete el.dataset.passPills;
    return;
  }
  ctx.save();
  ctx.font = FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const [slot, seat] of layout.seats.entries()) {
    if (!passed.has(seat.id)) continue;
    const r = bubbleRectForSeat(seat);
    drawn.push(`${slot}:${r.x},${r.y},${r.w},${r.h}`);
    ctx.fillStyle = COLOR_BG;
    ctx.beginPath();
    // rounded pill
    const rad = r.h / 2;
    ctx.moveTo(r.x + rad, r.y);
    ctx.arcTo(r.x + r.w, r.y, r.x + r.w, r.y + r.h, rad);
    ctx.arcTo(r.x + r.w, r.y + r.h, r.x, r.y + r.h, rad);
    ctx.arcTo(r.x, r.y + r.h, r.x, r.y, rad);
    ctx.arcTo(r.x, r.y, r.x + r.w, r.y, rad);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = COLOR_BORDER;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = COLOR_TEXT;
    ctx.fillText('Pass', r.x + r.w / 2, r.y + r.h / 2 + 0.5);
  }
  ctx.restore();
  if (el && el.dataset) el.dataset.passPills = drawn.join(';');
}

export const _internals = { BUBBLE_W, BUBBLE_H };
